import React, { useState } from 'react';
import { X, Send, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { createTicket } from '../../services/ticketService';
import { TicketType, TicketPriority } from '../../types/ticket';
import toast from 'react-hot-toast';

interface RaiseTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialType?: TicketType;
  userType?: 'jobseeker' | 'hr' | 'admin';
}

const RaiseTicketModal: React.FC<RaiseTicketModalProps> = ({
  isOpen,
  onClose,
  initialType = 'general',
  userType = 'jobseeker',
}) => {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    type: initialType,
    subject: '',
    description: '',
    priority: 'medium' as TicketPriority,
  });

  // HR-specific ticket types
  const hrTicketTypes: { value: TicketType; label: string }[] = [
    { value: 'technical', label: 'Report technical issues or bugs' },
    { value: 'general', label: 'Ask for feature requests' },
    { value: 'account', label: 'Seek account-related support' },
    { value: 'other', label: 'Report candidate or recruiter issues' },
    { value: 'general', label: 'Ask for data corrections' },
    { value: 'general', label: 'Give feedback or suggestions' },
  ];

  // Default ticket types for job seekers
  const defaultTicketTypes: { value: TicketType; label: string }[] = [
    { value: 'technical', label: 'Technical Support' },
    { value: 'general', label: 'General Query' },
    { value: 'account', label: 'Account Issues' },
    { value: 'billing', label: 'Billing & Payments' },
    { value: 'other', label: 'Other' },
  ];

  const ticketTypes = userType === 'hr' ? hrTicketTypes : defaultTicketTypes;

  const priorities: { value: TicketPriority; label: string; color: string }[] = [
    { value: 'low', label: 'Low', color: 'text-gray-600' },
    { value: 'medium', label: 'Medium', color: 'text-blue-600' },
    { value: 'high', label: 'High', color: 'text-orange-600' },
    { value: 'urgent', label: 'Urgent', color: 'text-red-600' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      toast.error('You must be logged in to raise a ticket');
      return;
    }

    if (!formData.subject.trim() || !formData.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const ticketId = await createTicket(
        user.id,
        user.email,
        user.name || 'User',
        user.userType || 'jobseeker',
        {
          type: formData.type,
          subject: formData.subject,
          description: formData.description,
          priority: formData.priority,
        }
      );

      toast.success('Ticket raised successfully! Our team will get back to you soon.');

      // Reset form
      setFormData({
        type: 'general',
        subject: '',
        description: '',
        priority: 'medium',
      });

      onClose();
    } catch (error: any) {

      // Show more specific error messages
      if (error?.code === 'permission-denied') {
        toast.error('Permission denied. Please ensure you are logged in correctly.');
      } else {
        toast.error(`Failed to raise ticket: ${error?.message || 'Please try again.'}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Raise a Support Ticket</h2>
            <p className="text-sm text-gray-600 mt-1">
              Our support team will respond within 24-48 hours
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={isSubmitting}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Ticket Type */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Ticket Type <span className="text-red-600">*</span>
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              required
            >
              {ticketTypes.map((type, index) => (
                <option key={`${type.value}-${index}`} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Subject <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              placeholder="Brief summary of your issue"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              required
              maxLength={100}
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.subject.length}/100 characters
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description <span className="text-red-600">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Please provide detailed information about your issue..."
              rows={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              required
              maxLength={1000}
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.description.length}/1000 characters
            </p>
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Priority
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {priorities.map((priority) => (
                <button
                  key={priority.value}
                  type="button"
                  onClick={() =>
                    setFormData({ ...formData, priority: priority.value })
                  }
                  className={`px-4 py-3 rounded-lg border-2 transition-all ${
                    formData.priority === priority.value
                      ? 'border-red-600 bg-red-50 font-semibold'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <span className={priority.color}>{priority.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold">Response Time</p>
              <p className="mt-1">
                Low/Medium priority: 24-48 hours • High priority: 12-24 hours • Urgent: 4-12 hours
              </p>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-3 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Submit Ticket</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RaiseTicketModal;